import CalendarIcon from "@assets/svg/calendar-alt.svg?react"
import { DateInput, type DateInputProps } from "@mantine/dates"
import { useIsMobile } from "@utils/device.utils"
import { isToday, isSameDay } from "date-fns"
import { useRef, useState } from "react"
import styles from "./CPDatepicker.module.css"

interface CPDatePickerProps extends Omit<DateInputProps, "getDayProps"> {
	isDateEnabled?: (date: Date) => boolean
}

export function CPDatePicker({
	onChange,
	classNames: consumerClassNames,
	isDateEnabled,
	value,
	...otherProps
}: CPDatePickerProps) {
	const [opened, setOpened] = useState(false)
	const [internalValue, setInternalValue] = useState<Date | null>(null)
	const isMobile = useIsMobile()
	const ref = useRef<HTMLInputElement>(null)

	const currentValue =
		value !== undefined ? (value as Date | null) : internalValue

	const handleChange = (date: Date | null) => {
		setInternalValue(date)
		onChange?.(date)
		setOpened(false)
	}

	const mergedClassNames = {
		...(typeof consumerClassNames === "object" ? consumerClassNames : {}),
		day: styles.selectedDay,
		input: [
			styles.uppercaseInput,
			typeof consumerClassNames === "object"
				? (consumerClassNames as Record<string, string>)?.input
				: undefined
		]
			.filter(Boolean)
			.join(" ")
	}

	const getDayProps = (date: Date) => {
		const isSelected =
			currentValue instanceof Date && isSameDay(date, currentValue)
		const highlighted = isSelected || (!currentValue && isToday(date))
		const enabled = isDateEnabled?.(date) ?? true

		return {
			style: {
				color: highlighted
					? "var(--mantine-color-white)"
					: !enabled
						? "var(--mantine-color-dimmed)"
						: "var(--mantine-color-text)",
				backgroundColor: highlighted
					? "var(--mantine-color-text)"
					: enabled
						? "var(--mantine-color-primary)"
						: undefined
			}
		}
	}

	return (
		<DateInput
			ref={ref}
			tabIndex={0}
			rightSection={
				<CalendarIcon
					fill="var(--text-color-primary)"
					style={{ cursor: "pointer" }}
					onClick={() => setOpened(true)}
				/>
			}
			rightSectionPointerEvents="all"
			numberOfColumns={isMobile ? 1 : 2}
			valueFormat="DDMMMYYYY"
			firstDayOfWeek={0}
			placeholder="Select a date"
			{...otherProps}
			value={currentValue}
			onChange={handleChange}
			classNames={mergedClassNames}
			getDayProps={getDayProps}
			popoverProps={{
				opened,
				onOpen: () => setOpened(true),
				onDismiss: () => {
					setOpened(false)
					ref.current?.focus()
				}
			}}
			onKeyDown={(e) => {
				if (e.key === "Enter") {
					e.preventDefault()
					setOpened(true)
				}
				if (e.key === "Escape") {
					setOpened(false)
					ref.current?.focus()
				}
			}}
		/>
	)
}
